// expansion panel style CheckoutCard
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';

import Button from './button';
import { Accordion, AccordionSummary, AccordionActions } from './accordion';

export const CheckoutAccordionContext = React.createContext();

export function CheckoutAccordion(props) {
  const { onChange, expanded } = props;
  const {
    defaultCancel = true,
    defaultButton = true,
    hideToggles = false,
    onCancel,
    onSave,
    expandText,
    cancelText,
    onExpand,
    crossfade = false,
    dataCyName = 'unknown',
    ...other
  } = props;

  const contextValue = useMemo(
    () => ({
      defaultCancel,
      defaultButton,
      hideToggles,
      onChange,
      onCancel,
      onSave,
      expanded,
      expandText,
      cancelText,
      onExpand,
      crossfade,
      dataCyName,
    }),
    [
      crossfade,
      dataCyName,
      defaultCancel,
      defaultButton,
      hideToggles,
      onChange,
      onCancel,
      onSave,
      expanded,
      expandText,
      cancelText,
      onExpand,
    ]
  );

  return (
    <CheckoutAccordionContext.Provider value={contextValue}>
      <Accordion {...other} />
    </CheckoutAccordionContext.Provider>
  );
}

export function CheckoutAccordionSummary(props) {
  const { children, ...other } = props;
  const {
    defaultCancel,
    hideToggles,
    onCancel,
    onChange,
    expanded,
    expandText = 'Change',
    cancelText = 'Cancel',
    onExpand,
    crossfade,
    dataCyName,
  } = useContext(CheckoutAccordionContext);

  function handleCancel(e) {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    } else if (onChange) {
      onChange();
    }
  }

  function handleExpand() {
    if (onExpand) {
      onExpand();
    }
  }

  if (crossfade) {
    return (
      <CrossfadeSummary tabIndex={-1} {...other}>
        <div className='summaryHider'>{children}</div>
        {!hideToggles && defaultCancel && expanded && (
          <CancelButton
            data-cy={`expanded-cancel-${dataCyName}`}
            data-test={`expanded-cancel-${dataCyName}`}
            data-testid={`expanded-cancel-${dataCyName}`}
            onClick={handleCancel}
          >
            {cancelText}
          </CancelButton>
        )}
        {!hideToggles && onExpand && !expanded && (
          <ExpandText
            data-cy={`expand-text-${dataCyName}`}
            data-test={`expand-text-${dataCyName}`}
            data-testid={`expanded-text-${dataCyName}`}
            onClick={handleExpand}
          >
            {expandText}
          </ExpandText>
        )}
      </CrossfadeSummary>
    );
  }

  return (
    <StyledAccordionSummary tabIndex={-1} {...other}>
      {children}
      {!hideToggles && defaultCancel && expanded && <CancelButton onClick={handleCancel}>{cancelText}</CancelButton>}
      {!hideToggles && onExpand && !expanded && (
        <ExpandText
          data-cy={`expand-text-${dataCyName}`}
          data-test={`expand-text-${dataCyName}`}
          data-testid={`expand-text-${dataCyName}`}
          onClick={handleExpand}
        >
          {expandText}
        </ExpandText>
      )}
    </StyledAccordionSummary>
  );
}

export function CheckoutAccordionActions(props) {
  const { children, className = '', ...other } = props;
  const { defaultButton, onSave, crossfade, expanded, dataCyName } = useContext(CheckoutAccordionContext);

  function renderContents() {
    return (
      <Grid container>
        <Grid
          data-cy={`expand-details-${dataCyName}`}
          data-test={`expand-details-${dataCyName}`}
          data-testid={`expand-details-${dataCyName}`}
          item
          xs={12}
        >
          {children}
        </Grid>
        <Grid item xs={12}>
          {defaultButton && (
            <StyledButton
              width='73px'
              height='33px'
              onClick={onSave}
              data-cy={`checkout-expansion-save-${dataCyName}`}
              data-test={`checkout-expansion-save-${dataCyName}`}
              data-testid={`checkout-expansion-save-${dataCyName}`}
            >
              Save
            </StyledButton>
          )}
        </Grid>
      </Grid>
    );
  }

  if (crossfade) {
    return (
      <CrossfadeDetails className={`${className} ${expanded ? 'expanded' : ''}`} {...other}>
        {renderContents()}
      </CrossfadeDetails>
    );
  }

  return <AccordionActions {...other}>{renderContents()}</AccordionActions>;
}

CheckoutAccordion.testProps = [
  {
    children: [
      <CheckoutAccordionSummary>Summary</CheckoutAccordionSummary>,
      <CheckoutAccordionActions>Details</CheckoutAccordionActions>,
    ],
  },
  {
    children: [
      <CheckoutAccordionSummary>Summary</CheckoutAccordionSummary>,
      <CheckoutAccordionActions>Details</CheckoutAccordionActions>,
    ],
    expanded: true,
  },
];

CheckoutAccordionSummary.snapshot = false;
CheckoutAccordionActions.snapshot = false;

const StyledAccordionSummary = styled(AccordionSummary)`
  align-items: center;
`;

const CrossfadeSummary = styled(AccordionSummary)`
  margin-bottom: -36px !important;
  & .summaryHider {
    display: flex;
    width: 100%;
    opacity: 1;
    transition-property: opacity;
    transition-duration: 300ms;
  }
  &.Mui-expanded .summaryHider {
    opacity: 0;
  }

  &.Mui-focused {
    background-color: transparent !important;
  }
`;

const CrossfadeDetails = styled(AccordionActions)`
  opacity: 0;
  position: relative;
  transition-property: opacity;
  transition-duration: 300ms;
  &.expanded {
    opacity: 1;
  }
`;

const CancelButton = styled.button`
  align-items: flex-start;
  appearance: none;
  background-color: transparent;
  border: none;
  color: #848f97;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-family: ${({ theme }) => theme?.customized?.fonts?.primary};
  margin: 0 0 0 auto;
  padding: 0;
  user-select: none;
  z-index: 2;
`;

export const ExpandText = styled(CancelButton)`
  color: #848f97;
  text-decoration: underline;
`;

export const StyledButton = styled(Button)`
  align-self: flex-start;
`;
